import { useState, useEffect, useMemo } from "react";
import { useToast } from "../hooks/use-toast";
import { getTradeRate } from "../services/digitalWalletService";
import { useIsMobile } from "../hooks/use-mobile";
import PairSelectionDialog from "./PairSelectionDialog";
import PairSelectionDropdown from "./PairSelectionDropdown";
import CurrencyFormatter from "../services/currencyFormatterService";

interface Asset {
  assetId: string;
  asset: string;
  [key: string]: any;
}

interface TradingPair {
  id: string;
  pair: string;
  asset: string;
  assetIcon?: string;
  assetName?: string;
  type?: string;
  availableAmount?: number;
  formattedAvailableAmount?: string;
  [key: string]: any;
}

interface TradeRate {
  rate: number;
  [key: string]: any;
}

interface TradePairProps {
  selectedAsset?: Asset | null;
  onSelect?: (pair: TradingPair) => void;
  setParentRate?: (rate: TradeRate) => void;
  typeOfTrade?: string;
  currentPairs?: TradingPair[] | null;
  zarBalance?: number;
  fiatLocalCurrencyBalance?: string;
  onDashboard?: boolean;
}

export function TradePair({
  selectedAsset,
  onSelect,
  setParentRate,
  typeOfTrade,
  currentPairs,
  zarBalance = 0,
  fiatLocalCurrencyBalance = "R 0.00",
  onDashboard = false,
}: TradePairProps) {
  const [selectedPair, setSelectedPair] = useState<TradingPair | null>(null);
  const [open, setOpen] = useState<boolean>(false);
  const { toast } = useToast();
  const isMobile = useIsMobile();

  useEffect(() => {
    setSelectedPair(null);
  }, [selectedAsset?.assetId]);

  useEffect(() => {
    if (!selectedPair || !currentPairs) return;

    const isValidPair = currentPairs.some(
      (pair: TradingPair) => pair.pair === selectedPair.pair
    );

    if (!isValidPair) {
      setSelectedPair(null);
    }
  }, [selectedPair, currentPairs]);

  useEffect(() => {
    if (!currentPairs || selectedPair) return;
    try {
      const firstFiatPair = currentPairs.find(
        (pair: TradingPair) => pair.type === "Fiat"
      );
      if (firstFiatPair) {
        setSelectedPair(firstFiatPair);

        if (onSelect) {
          onSelect(firstFiatPair);
        }
      }
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : String(err);
      toast({
        description: errorMessage,
        variant: "error",
      });
    }
  }, [currentPairs, selectedPair, onSelect, toast]);

  useEffect(() => {
    if (!selectedPair || !selectedAsset?.assetId) return;
    const fetchRate = async () => {
      try {
        const data = await getTradeRate(
          selectedAsset.assetId,
          "buy",
          selectedPair.pair
        );

        if (setParentRate) {
          setParentRate(data.data.data);
        }
      } catch (error) {
        const errorMessage =
          error instanceof Error ? error.message : String(error);
        console.error("Error fetching rate:", errorMessage);
      }
    };
    void fetchRate();
  }, [selectedPair, selectedAsset?.assetId, setParentRate, toast]);

  const handleSelect = (asset: TradingPair): void => {
    setSelectedPair(asset);
    if (onSelect) {
      onSelect(asset);
    }
    setOpen(false);
  };

  const displayBalance = useMemo(() => {
    if (!selectedPair) return "0.00";

    const isZarPair = selectedPair.asset === "zar";
    const pairBalance = selectedPair.availableAmount ?? 0;

    const fixZarDecimalSeparator = (value: string): string => {
      return value.replace(/,(\d{2})$/, ".$1");
    };

    const formatter = new CurrencyFormatter({
      locale: "en-US",
      decimalPlaces: 2,
      cryptocurrencyDecimalPlaces: 2,
      stablecoins: ["USDT", "USDC", "DAI"],
    });

    if (isZarPair && pairBalance === 0 && zarBalance > 0) {
      const formatted = formatter.format(zarBalance, "fiat", "ZAR");
      return fixZarDecimalSeparator(formatted);
    }

    if (selectedPair.formattedAvailableAmount) {
      let formatted = selectedPair.formattedAvailableAmount;
      if (isZarPair) {
        // Replace ZAR with R if present in the formatted string
        formatted = formatted.replace(/ZAR\s*/g, "R ");
        return fixZarDecimalSeparator(formatted);
      }
      return formatted;
    }

    if (selectedPair.availableAmount !== undefined) {
      if (isZarPair) {
        const formatted = formatter.format(
          selectedPair.availableAmount,
          "fiat",
          "ZAR"
        );
        return fixZarDecimalSeparator(formatted);
      }
      const assetSymbol = selectedPair.asset?.toUpperCase();
      const decimals = assetSymbol === "BTC" ? 6 : 2;
      return new Intl.NumberFormat("en-US", {
        minimumFractionDigits: decimals,
        maximumFractionDigits: decimals,
      })
        .format(selectedPair.availableAmount)
        .replace(/,/g, " ");
    }

    return "0.00";
  }, [selectedPair, zarBalance]);

  return (
    <>
      {typeOfTrade === "Buy" && (
        <div className="flex gap-4 my-2">
          <div
            className={`space-y-2 w-full ${onDashboard ? "md:w-[40%]" : "md:w-[30%]"}`}
          >
            <label className="text-text-secondary text-sm">Pay with</label>
            {isMobile ? (
              <PairSelectionDialog
                selectedPair={selectedPair}
                selectedAsset={selectedAsset}
                currentPairs={currentPairs}
                onPairSelect={handleSelect}
                open={open}
                onOpenChange={setOpen}
              />
            ) : (
              <PairSelectionDropdown
                selectedPair={selectedPair}
                selectedAsset={selectedAsset}
                currentPairs={currentPairs}
                onPairSelect={handleSelect}
                open={open}
                onOpenChange={setOpen}
              />
            )}
          </div>
          <div
            className={`space-y-2 w-full ${onDashboard ? "md:w-[60%]" : "md:w-[70%]"}`}
          >
            <label className="text-text-secondary text-sm">
              Available balance
            </label>
            <div className="py-3 md:py-4 px-4 md:px-6 rounded-2xl bg-gray-50 flex items-center h-[70px] md:h-[88px]">
              <div className="text-lg md:text-xl font-semibold text-[#6D6584] flex justify-between items-center w-full gap-2 md:gap-0">
                <p className="text-base md:text-lg">{displayBalance}</p>
                {!onDashboard && (
                  <p className="text-sm md:text-base">
                    ≈ {fiatLocalCurrencyBalance}
                  </p>
                )}
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
